import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { showSuccessToast } from '@app/components/CommonToast';
import LanguageSwitcher from '@app/components/LanguageSwitcher';
import { loadingScreen } from '@app/components/Loading';
import TnkButton from '@app/components/TnkButton';
import TnkDialog, { TNK_REFRESH_UNREAD_NOTIFICATIONS_EVENT } from '@app/components/TnkDialog';
import { INDEXDB_COMMON_KEYS, USER_ACTION } from '@app/consts/common_consts';
import { BIM_PROJECT_ID } from '@app/consts/env_consts';
import { removeRole } from '@app/features/rolesSlice';
import { EllipsisText } from '@app/hooks/useDataTable/dataTableHelper';
import { useNotificationSender } from '@app/hooks/useNotificationSender';
import { useTopBar } from '@app/hooks/useTopBar';
import { Cache } from '@app/modules/cache';
import { LANGUAGE_DEFAULT } from '@app/modules/i18n-locales/i18n';
import { NAVIGATE_ROUTE } from '@app/modules/routes/common-route';
import { UserUtils } from '@app/modules/userinfo';
import { authApiService } from '@app/services/authAps';
import {
  ConfirmedMessageListData,
  ConfirmedUsers,
  NotificationData,
  UpdateConfirmStatusPayload,
} from '@app/types/initScreen';
import { DateFormat, formatUtcTimeToLocal } from '@app/utils/DateUtils';
import logo from '@src/assets/imgs/Logo.jpg';
import { AppTopbarRef } from '@type/layout';
import { BreadCrumb } from 'primereact/breadcrumb';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

interface ConfirmedMessageItem {
  value: string;
  isHtml?: boolean;
}

/** Map URL path → breadcrumb i18n key (screenId). */
const PATH_TO_SCREEN_ID: { path: string; key: string }[] = [
  { path: NAVIGATE_ROUTE.SC003, key: 'sc003' },
  { path: NAVIGATE_ROUTE.BV002, key: 'bv002' },
  { path: NAVIGATE_ROUTE.SC001, key: 'sc001' },
  { path: NAVIGATE_ROUTE.SC002, key: 'sc002' },
  { path: NAVIGATE_ROUTE.SC004, key: 'sc004' },
  { path: NAVIGATE_ROUTE.SC005, key: 'sc005' },
  { path: NAVIGATE_ROUTE.SC006, key: 'sc006' },
  { path: NAVIGATE_ROUTE.SC007, key: 'sc007' },
  { path: NAVIGATE_ROUTE.SC008, key: 'sc008' },
  { path: NAVIGATE_ROUTE.SC009, key: 'sc009' },
  { path: NAVIGATE_ROUTE.SC010, key: 'sc010' },
  { path: NAVIGATE_ROUTE.SC011, key: 'sc011' },
  { path: NAVIGATE_ROUTE.SC012, key: 'sc012' },
  { path: NAVIGATE_ROUTE.SC013, key: 'sc013' },
  { path: NAVIGATE_ROUTE.SC014, key: 'sc014' },
  { path: NAVIGATE_ROUTE.BV001, key: 'bv001' },
];

function resolveScreenKey(pathname: string): string {
  const match = PATH_TO_SCREEN_ID.find(({ path }) => pathname === path || pathname.startsWith(path + '/'));
  return match?.key ?? 'currentPage';
}

/** Parse an HTML notification message, replacing any http/https URLs inline with <a> tags for link rendering. */
function parseHtmlMessage(text: string): ConfirmedMessageItem {
  // Remove stray quotes and replace URLs with <a> tags, while also formatting any ISO 8601 date strings to local time.
  const messageWithLinks = text
    .trim()
    .replaceAll('"', '')
    .replace(/(https?:\/\/[^\s<"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/(\d{4}[-/]\d{2}[-/]\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?Z?)/g, (dateStr) =>
      formatUtcTimeToLocal(dateStr, DateFormat.YEAR_MONTH_DAY_HOUR_MINUTE)
    );
  // Return the formatted message with isHtml set to true to indicate that it contains HTML content.
  return {
    value: messageWithLinks,
    isHtml: true,
  };
}

/** Strip HTML tags, decode common HTML entities, and remove stray quote characters from a message string. */
function stripHtml(html?: string | null): string {
  // If the input is null or undefined, return an empty string to avoid errors
  if (!html) return '';
  // Remove <br> tags, all other HTML tags, decode common HTML entities, remove stray quotes, and normalize whitespace
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]{0,2000}>/g, '')
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&nbsp;', ' ')
    .replace(/&quot;/gi, '')
    .replaceAll('&#34;', '')
    .replaceAll('&#39;', "'")
    .replace(/[\u201C\u201D"]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const AppTopbar = forwardRef<AppTopbarRef>((_, ref) => {
  // i18n and navigation hooks
  const { t, i18n } = useTranslation();
  // React Router navigation hook
  const navigate = useNavigate();
  // Redux dispatch hook
  const dispatch = useDispatch();
  // Sync projectId from URL param to localStorage if changed
  const [searchParams] = useSearchParams();
  // Refs for menu buttons and overlay panels
  const { t: tb } = useTranslation('breadcrumb');
  // Refs for menu buttons and overlay panels
  const menubuttonRef = useRef(null);
  // Refs for menu buttons and overlay panels
  const topbarmenuRef = useRef(null);
  // Refs for menu buttons and overlay panels
  const topbarmenubuttonRef = useRef(null);
  // Refs for user menu overlay panels
  const userMenuRef = useRef<OverlayPanel>(null);
  // Ref for notification overlay panel
  const notificationRef = useRef<OverlayPanel>(null);
  // Ref for notification sender overlay panel
  const notificationSenderRef = useRef<OverlayPanel>(null);
  // State for loading status of notification sender
  const { pathname } = useLocation();
  const isCommonErrorScreen = pathname === NAVIGATE_ROUTE.SC014;
  // State to control visibility of notification detail dialog
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const [loadingMessageDetail, setLoadingMessageDetail] = useState<boolean>(false);
  // Custom hook for App Top Bar
  useTopBar({ enabled: !isCommonErrorScreen });

  // Helper function to get language-specific message for confirmed messages
  const getLocalizedMessage = (item: ConfirmedMessageListData) => {
    return i18n.language === LANGUAGE_DEFAULT ? (item.summaryMessageJp ?? '') : (item.summaryMessageEn ?? '');
  };

  // Helper function to get language-specific notification message
  const getLocalizedNotificationMessage = (notification?: NotificationData) => {
    return i18n.language === LANGUAGE_DEFAULT
      ? (notification?.summaryMessageJp ?? '')
      : (notification?.summaryMessageEn ?? '');
  };

  // Helper function to build tooltip content for confirmed users
  const buildTooltipContent = (rows: ConfirmedUsers[]): string => {
    return rows
      .map((item) => {
        const displayName = item?.fullname ?? '';
        return t('common.confirmedCountTooltip', { 0: displayName });
      })
      .join('\n');
  };

  // Helper function to format text with links and line breaks for notification messages
  const formatTextWithLinksAndBreaks = (text?: string): ConfirmedMessageItem | null => {
    // If text is empty or undefined, return null to indicate no content
    if (!text) return null;

    // If text contains HTML tags (other than <br>), delegate to parseHtmlMessage for URL extraction and rendering
    if (/<(?!br\s*\/?>)[a-z][^>]*/i.test(text)) {
      return parseHtmlMessage(text);
    }

    // If text does not contain HTML tags, return the plain text with isHtml set to false
    return {
      value: text,
      isHtml: false,
    };
  };

  // Helper function to extract the title message based on the current language setting
  const getTitleMessage = (notification: NotificationData | null): string => {
    return i18n.language === LANGUAGE_DEFAULT
      ? (notification?.titleMessageJp ?? '')
      : (notification?.titleMessageEn ?? '');
  };

  // Helper function to extract the detail message based on the current language setting
  const getDetailMessage = (notification: NotificationData | null): string => {
    return i18n.language === LANGUAGE_DEFAULT
      ? (notification?.detailMessageJp ?? '')
      : (notification?.detailMessageEn ?? '');
  };

  // Format the detail message to extract key-value pairs and render with proper styling and link support
  const messageDetail = formatTextWithLinksAndBreaks(getDetailMessage(selectedNotification));

  // Helper function to get badge color class based on confirmation status
  const getBadgeColorClass = (confirmedCount: number, countRecipient: number): string => {
    if (confirmedCount === countRecipient) {
      return 'send-panel__badge--green'; // All recipients confirmed
    } else if (confirmedCount > 0 && confirmedCount < countRecipient) {
      return 'send-panel__badge--orange'; // Partial confirmation
    } else {
      return 'send-panel__badge--blue'; // No one confirmed
    }
  };

  // Helper function to get button class name based on unread count
  const getUnreadButtonClassName = (unreadCount?: number): string => {
    if (!unreadCount || unreadCount === 0) {
      return 'btn-icon-flat-light btn mr-2'; // No badge, default size
    }
    if (unreadCount === 100) {
      return 'btn-icon-flat-light btn-icon-flat-light__btn-icon-three-number btn mr-2'; // (3 digits)
    }
    if (unreadCount > 100) {
      return 'btn-icon-flat-light btn-icon-flat-light__btn-icon-three-number-plus btn mr-2'; // (3 digits with plus sign)
    }
    if (unreadCount >= 10) {
      return 'btn-icon-flat-light btn-icon-flat-light__btn-icon-two-number btn mr-2'; // (2 digits)
    }
    return 'btn-icon-flat-light btn mr-2'; // (1 digit)
  };

  // Helper function to get badge class name based on unread count
  const getUnreadBadgeClassName = (unreadCount?: number): string => {
    const baseClass = 'p-badge-danger';
    if (!unreadCount || unreadCount === 0) {
      return baseClass;
    }
    if (unreadCount === 100) {
      return `${baseClass} p-badge__three-number`; // (3 digits)
    }
    if (unreadCount > 100) {
      return `${baseClass} p-badge__three-number-plus`; // (3 digits with plus sign)
    }
    if (unreadCount >= 10) {
      return `${baseClass} p-badge__two-number`; // (2 digits)
    }
    return baseClass; // (1 digit)
  };

  // Helper function to format unread badge count
  const getUnreadBadgeText = (unreadCount?: number): string => {
    // If the unread count is 0, the UnreadCount badge will not be displayed.
    if (!unreadCount || unreadCount === 0) {
      return '';
    }
    // If the unread count is greater than 99: Display 99+ on the UnreadCount badge.
    if (unreadCount > 99) {
      return '99+';
    }
    // If the unread count is ≥ 1, the count will be displayed in the upper right corner of the icon as a red circle.
    // If the unread count is between 1 and 99: display that exact number on the UnreadCount badge.
    return unreadCount.toString();
  };

  // Custom hook to manage notification sender data and API calls
  const {
    loadingReadAllMessages,
    showNotificationDialog,
    confirmedMessageListData,
    unreadMessageData,
    setShowNotificationDialog,
    setLoadingConfirmedMessageList,
    setLoadingUnreadMessage,
    readAllMessagesApi,
    updateReadStatusApi,
    updateConfirmStatusApi,
  } = useNotificationSender();

  // DD 4.1 item 4 v1.2: single listener in AppTopbar to avoid multiple hook instances registering duplicate listeners
  useEffect(() => {
    const handler = () => setLoadingUnreadMessage?.(true);
    window.addEventListener(TNK_REFRESH_UNREAD_NOTIFICATIONS_EVENT, handler);
    return () => window.removeEventListener(TNK_REFRESH_UNREAD_NOTIFICATIONS_EVENT, handler);
  }, []);

  // Resolve current screen label from pathname using 画面一覧.md mapping
  const screenKey = resolveScreenKey(pathname);
  // Breadcrumb items for topbar, using resolved screen key for current page label
  const breadCrumbItems = [
    {
      label: tb('home'),
      template: () => <Link to="/">{tb('home')}</Link>,
    },
    { label: tb(screenKey), template: () => <span>{tb(screenKey)}</span> },
  ];

  // Handlers to toggle visibility of user menu and notification sender overlay panels
  const toggleUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Close other panels before opening user menu
    notificationRef.current?.hide();
    notificationSenderRef.current?.hide();
    userMenuRef.current?.toggle(event);
  };

  // Handler to toggle notification overlay panel
  const toggleNotification = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Close other panels before opening notification panel
    userMenuRef.current?.hide();
    notificationSenderRef.current?.hide();
    if (!showNotificationDialog) {
      notificationRef.current?.toggle(event);
    }
  };

  // Handler to toggle notification sender overlay panel
  const toggleNotificationSender = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Close other panels before opening notification sender panel
    userMenuRef.current?.hide();
    notificationRef.current?.hide();
    notificationSenderRef.current?.toggle(event);
  };

  // Handler for when notification sender overlay panel is hidden, to reset loading state
  const handleNotificationSenderHide = () => {
    if (isCommonErrorScreen) {
      return;
    }
    setLoadingConfirmedMessageList?.(true);
  };

  // Handler for when notification overlay panel is hidden, to reset loading state
  const handleUnreadMessageHide = () => {
    if (isCommonErrorScreen) {
      return;
    }
    // Reset loading state when notification panel is closed and call API to refresh unread message list to ensure data is up-to-date when user opens the panel again
    setLoadingUnreadMessage?.(true);
  };

  // Handler for user logout action
  const handleLogout = async () => {
    loadingScreen.show();
    let ssoLogoutUrl;
    try {
      const response = await authApiService.logout();
      ssoLogoutUrl = response?.data?.ssoLogoutUrl;
      // Remove projectId from localStorage on logout to prevent unauthorized access after logout, especially when SSO logout API fails or is not implemented.
      localStorage.removeItem(BIM_PROJECT_ID);
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local cleanup even if API fails
    } finally {
      // Always clear user data and redirect regardless of API result
      await Cache.delete(INDEXDB_COMMON_KEYS.USER_INFO, 'indexdb');
      UserUtils.set(null);
      dispatch(removeRole());
      userMenuRef.current?.hide();
      loadingScreen.hide();
      showSuccessToast(t('MSG_INF_SC001_00002', { ns: 'messages' }));

      // Remove user when logout
      UserUtils.set(null);

      // Redirect to SSO logout URL if provided, otherwise fallback to login page
      if (ssoLogoutUrl) {
        globalThis.location.href = ssoLogoutUrl;
      } else {
        navigate(NAVIGATE_ROUTE.SC001); // Fallback to login page if no SSO logout URL provided
      }
    }
  };

  // Expose refs to parent component through useImperativeHandle
  useImperativeHandle(ref, () => ({
    menubutton: menubuttonRef.current,
    topbarmenu: topbarmenuRef.current,
    topbarmenubutton: topbarmenubuttonRef.current,
  }));

  // 4.3.3. Displaying notification details when a notification is clicked.
  const handleNotificationClick = useCallback(
    async (notification: NotificationData) => {
      // Open the dialog box that displays the details of the notification.
      setShowNotificationDialog?.(true);
      // Open the dialog box that displays the details of the notification.
      setLoadingMessageDetail(true);
      // Check if the readFlg (read) status is false (unread).
      if (notification?.readFlg === false) {
        // If the notification is unread, call API-NOTIFY-03 to update the read status for that specific notification.
        const responseData = await updateReadStatusApi?.({ notificationIconId: notification?.notificationIconId });
        // After successfully updating the read status, update the local state to reflect the change immediately in the UI without waiting for the next data fetch cycle.
        if (responseData) {
          setLoadingMessageDetail(false);
          // Use the updatedAt from the API response if available, otherwise fallback to the existing notification's updatedAt
          const updatedAt = responseData?.updatedAt ?? notification?.updatedAt;
          // Open the dialog box that displays the details of the notification with updated data
          setSelectedNotification({ ...notification, updatedAt });
        }
      } else {
        // If already read, just open the dialog with existing data
        setSelectedNotification(notification);
        // Open the dialog box that displays the details of the notification.
        setShowNotificationDialog?.(true);
        // Set loading to false since we are not making an API call to update read status
        setLoadingMessageDetail(false);
      }
    },
    [updateReadStatusApi, setSelectedNotification, setShowNotificationDialog]
  );

  // Handler to close notification detail dialog
  const handleCloseNotificationDialog = () => {
    // Close the dialog box that displays the details of the notification.
    setShowNotificationDialog?.(false);
    // Reset selected notification to null when dialog is closed to clear the data
    setSelectedNotification(null);
    // Note: API-NOTIFY-01 refresh is handled globally via TNK_DIALOG_HIDE_EVENT in useNotificationSender
  };

  // Handler to confirm notification
  const handleConfirmNotification = useCallback(async () => {
    // Close the dialog box that displays the details of the notification.
    handleCloseNotificationDialog();
    // Call API-NOTIFY-06 to update for the message.
    const payload: UpdateConfirmStatusPayload = {
      notificationIconId: String(selectedNotification?.notificationIconId),
      confirmFlg: true,
      updatedAt: selectedNotification?.updatedAt ?? '',
    };
    await updateConfirmStatusApi?.(payload);
  }, [selectedNotification, updateConfirmStatusApi]);

  // 4.3.2. Mark all notifications as read.
  const handleReadAllMessages = useCallback(async () => {
    // Call API-NOTIFY-04 to update the read status for all unread messages.
    await readAllMessagesApi?.();
  }, [readAllMessagesApi]);

  // On component mount and whenever searchParams change, check for projectId in URL and sync to localStorage.
  // Exception — SC009 § 4.1.1 (v1.5): SC009 accepts a `projectId` URL param for read-only detail
  // viewing (e.g. from SC011's プロジェクト情報 row action) without switching the Sidebar's currently
  // selected project, so this global sync must be skipped while on SC009.
  useEffect(() => {
    if (pathname === NAVIGATE_ROUTE.SC009) return;
    const urlProjectId = searchParams.get('projectId');
    if (urlProjectId && localStorage.getItem('projectId') !== urlProjectId) {
      localStorage.setItem('projectId', urlProjectId);
    }
  }, [searchParams, pathname]);

  // Listen for F5 key press to refresh the confirmed message list
  useEffect(() => {
    if (isCommonErrorScreen) {
      // CM001 v1.1: do not fetch notification data on SC014 and ensure dialogs are closed.
      notificationRef.current?.hide();
      notificationSenderRef.current?.hide();
      handleCloseNotificationDialog();
      setLoadingConfirmedMessageList?.(false);
      setLoadingUnreadMessage?.(false);
      return;
    }

    setLoadingConfirmedMessageList?.(true);
    setLoadingUnreadMessage?.(true);
  }, [isCommonErrorScreen, pathname, setLoadingConfirmedMessageList, setLoadingUnreadMessage]);

  // Effect to log application version on mount and when version changes
  useEffect(() => {
    console.log('Version: ', import.meta.env.VITE_VER);
  }, [import.meta.env.VITE_VER]);

  return (
    <div className="layout-topbar">
      <div className="flex">
        <a href="/" className="mr-2 flex align-items-center">
          <img src={logo} width="40" height="40" alt="logo" />
        </a>
        {!isCommonErrorScreen && <BreadCrumb model={breadCrumbItems} className="topbar-breadcrumb" />}
      </div>
      <div className="ml-auto flex border-l pl-3">
        {!isCommonErrorScreen && (
          <>
            {/* Notification sender button */}
            <Button
              icon="pi pi-send"
              text
              severity="secondary"
              onClick={toggleNotificationSender}
              className="btn-icon-flat-light btn mr-2"
            ></Button>
            {/* Open the dialog box that displays the list of notifications bell button */}
            <Button
              icon="pi pi-bell"
              text
              severity="secondary"
              onClick={toggleNotification}
              className={getUnreadButtonClassName(unreadMessageData?.unreadCount)}
              badge={getUnreadBadgeText(unreadMessageData?.unreadCount)}
              badgeClassName={getUnreadBadgeClassName(unreadMessageData?.unreadCount)}
            ></Button>
          </>
        )}
        {/* Language switcher component for changing application language */}
        <LanguageSwitcher buttonClassName="btn-icon-flat-light btn mr-2" />
        {/* User menu button */}
        <Button
          icon="pi pi-user"
          text
          severity="secondary"
          onClick={toggleUserMenu}
          aria-label="User Menu"
          className="btn-icon-flat-light btn ml-auto"
        />
        {/* User menu overlay panel */}
        <OverlayPanel ref={userMenuRef} style={{ width: '244px' }} className="user-menu-overlay">
          <div className="user-menu-container">
            <div className="user-menu-header">
              <h3 className="user-menu-title">{t('userMenu.account')}</h3>
            </div>
            {UserUtils.get()?.user && (
              <div className="user-menu-info">
                <div className="user-menu-item">
                  <span className="user-menu-row">
                    <span className="label">{t('userMenu.username')}:</span>
                    <span className="value">
                      <EllipsisText>{UserUtils?.get()?.user.fullName}</EllipsisText>
                    </span>
                  </span>
                </div>
                <div className="user-menu-item">
                  <span className="user-menu-row">
                    <span className="label">{t('userMenu.userRole')}:</span>
                    <span className="value">
                      <EllipsisText>
                        {UserUtils?.get()
                          ?.systemRoles?.map((role: any) =>
                            i18n.language === LANGUAGE_DEFAULT ? role.roleNameJp : role.roleNameEn
                          )
                          .join(', ')}
                      </EllipsisText>
                    </span>
                  </span>
                </div>
              </div>
            )}
            <div className="user-menu-btn">
              <TnkButton buttonType="secondary" fullWidth label={t('userMenu.logout')} onClick={handleLogout} />
            </div>
          </div>
        </OverlayPanel>
        {!isCommonErrorScreen && (
          <>
            {/* Notification sender overlay panel */}
            <OverlayPanel ref={notificationSenderRef} className="send-overlay" onHide={handleNotificationSenderHide}>
              <div className={`send-panel ${confirmedMessageListData?.length === 0 ? 'send-panel--empty' : ''}`}>
                <div className="send-panel__header">
                  <div className="send-panel__header-container">
                    <h3 className="send-panel__title" id="lblSharedNotificationTitle">
                      {t('common.notificationSender')}
                    </h3>
                  </div>
                </div>
                <div
                  className={`send-panel__body ${confirmedMessageListData?.length === 0 ? 'send-panel__body--empty' : ''}`}
                >
                  <Tooltip
                    target=".send-panel__badge-label[data-pr-tooltip]"
                    className="custom-tooltip data-table-tooltip"
                    position="bottom"
                    style={{ marginTop: '10px' }}
                  />
                  {confirmedMessageListData?.map((item) => (
                    <div key={item.notificationIconId} className="send-panel__item">
                      <div className="send-panel__item-header">
                        <div
                          className="send-panel__item-title"
                          id="lblCardContent"
                          dangerouslySetInnerHTML={{ __html: getLocalizedMessage(item) }}
                        />
                        <div
                          className={`send-panel__badge ${getBadgeColorClass(item?.confirmedCount ?? 0, item?.countRecipient ?? 0)}`}
                          id="lblConfirmedCount"
                        >
                          <span
                            className="send-panel__badge-label"
                            {...((item?.confirmedUsers?.length ?? 0) > 0 && {
                              'data-pr-tooltip': buildTooltipContent(item?.confirmedUsers ?? []),
                            })}
                          >
                            {`${item?.confirmedCount ?? 0}/${item?.countRecipient ?? 0}`}
                          </span>
                        </div>
                      </div>
                      <div className="send-panel__item-time" id="lblSharedDateTime">
                        {formatUtcTimeToLocal(item?.createdAt ?? '', DateFormat.YEAR_MONTH_DAY_HOUR_MINUTE)}
                      </div>
                    </div>
                  ))}
                  {confirmedMessageListData?.length === 0 && (
                    <div className="send-panel__empty" id="lblNoNotification">
                      {t('common.noNotification')}
                    </div>
                  )}
                </div>
              </div>
            </OverlayPanel>
            {/* Notification panel */}
            <OverlayPanel
              ref={notificationRef}
              className={`notification-panel-overlay ${unreadMessageData?.notificationList?.length === 0 ? 'notification-panel-overlay--empty' : ''}`}
              onHide={handleUnreadMessageHide}
            >
              <div className="notification-panel">
                <div className="notification-panel__header">
                  <div className="notification-panel__header-container">
                    <h3 className="notification-panel__title" id="lblNotificationBoxTitle">
                      {t('common.notificationTitle')}
                    </h3>
                    {/** Display the "すべて既読にする" (Mark all as read) button if there is at least one unread notification. */}
                    {(unreadMessageData?.notificationList?.filter((notification) => !notification?.readFlg).length ??
                      0) > 0 && (
                      <button
                        type="button"
                        className="notification-panel__mark-read"
                        id="btnMarkAllAsRead"
                        onClick={handleReadAllMessages}
                      >
                        {t('common.markAllRead')}
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className={`notification-panel__body ${unreadMessageData?.notificationList?.length === 0 ? 'notification-panel__body--empty' : ''}`}
                >
                  {loadingReadAllMessages ? (
                    <div className="notification-panel__loading">
                      <ProgressSpinner />
                    </div>
                  ) : (
                    <>
                      {unreadMessageData?.notificationList?.map((notification) => (
                        <button
                          key={`notification-${notification?.notificationIconId}`}
                          className={`notification-panel__item ${notification?.readFlg ? '' : 'notification-panel__item--unread'}`}
                          onClick={() => handleNotificationClick(notification)}
                          type="button"
                        >
                          <div
                            className={`notification-panel__item-message ${notification?.readFlg ? '' : 'notification-panel__item-message--unread'}`}
                            id="lblNotificationSummary"
                            dangerouslySetInnerHTML={{ __html: getLocalizedNotificationMessage(notification) }}
                          />
                          <div className="notification-panel__item-time" id="lblNotificationDate">
                            {formatUtcTimeToLocal(notification?.createdAt ?? '', DateFormat.YEAR_MONTH_DAY_HOUR_MINUTE)}
                          </div>
                        </button>
                      ))}
                      {unreadMessageData?.notificationList?.length === 0 && (
                        <div className="notification-panel__empty" id="lblNoNotification">
                          {t('MSG_INFO_COM_00113', { ns: 'messages', 0: t('common.noNotificationMessage') })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </OverlayPanel>
            {/* Notification detail dialog */}
            <TnkDialog
              header={
                loadingMessageDetail ? (
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '18px' }}></i>
                ) : (
                  <EllipsisText tooltipText={getTitleMessage(selectedNotification)}>
                    {getTitleMessage(selectedNotification)}
                  </EllipsisText>
                )
              }
              visible={showNotificationDialog}
              style={{ width: '436px' }}
              onHide={handleCloseNotificationDialog}
              modal
              className="notification-detail-dialog"
              draggable={false}
            >
              <div className="notification-detail">
                {loadingMessageDetail ? (
                  <div className="notification-detail__loading">
                    <ProgressSpinner />
                  </div>
                ) : (
                  <>
                    {/* Header have url */}
                    {messageDetail && messageDetail.isHtml && (
                      <div className="notification-detail__section">
                        <div
                          className="notification-detail__value"
                          dangerouslySetInnerHTML={{ __html: messageDetail.value }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              {!loadingMessageDetail && (
                <div className="notification-detail__footer">
                  {selectedNotification?.eventType?.trim() === USER_ACTION.USER_DELETE ||
                  selectedNotification?.eventType?.trim() === USER_ACTION.ACCOUNT_DEACTIVATE ? (
                    <></>
                  ) : (
                    <Button
                      id="btnConfirm"
                      label={t('btnConfirm')}
                      onClick={handleConfirmNotification}
                      className="btn btn-primary btn-confirm"
                      disabled={Boolean(selectedNotification?.confirmFlg) === true}
                    />
                  )}
                </div>
              )}
            </TnkDialog>
          </>
        )}
      </div>
    </div>
  );
});

AppTopbar.displayName = 'AppTopbar';

export default AppTopbar;
